import React from "react";
import { getRequest, getURL, postRequest } from "../functions/api_functions";
import { Box, IconButton, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import GroupsIcon from "@mui/icons-material/Groups";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import LinearProgressBar from "./progress_component";
import { Ischools } from "../interfaces/interface";
import AlertMessage from "./alert_component";
import SchoolForm from "./form_schools";
import CustomDialog from "./dialog_component";
// import Students from "./students";
import Students from "./students_new";
import CustomBackDrop from "./backdrop";

// Tipo de resposta da API
type SchoolResponse = {
  status_code: number;
  school_link: string;
};

const SchoolTable: React.FC = () => {
  const [schools, setSchools] = React.useState<Ischools[]>([]);
  const [alertMessage, setAlertMessage] = React.useState<{ message: string; type: "success" | "error" | "info"; close: boolean } | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogContent, setDialogContent] = React.useState<React.ReactNode>(null);
  const [schoolLink, setSchoolLink] = React.useState('');
  const [updateSchool, setUpdateSchool] = React.useState<Ischools | null>(null);

  // Função para buscar dados da API
  const fetchSchools = async () => {
    setLoading(true);
    try {
      const response = await getRequest<{ status_code: number; schools: Ischools[] }>("/get_all_schools/");
      if (response.status_code === 200) {
        setSchools(response.schools);
      } else {
        console.error("Erro ao buscar escolas.");
      }
    } catch (error) {
      console.error("Erro na requisição:", error);
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar os dados da escola
  const modifySchools = async (school: Ischools) => {
    setAlertMessage({ message: 'Aguarde...', type: "info", close: false });
    try {
      const response = await postRequest<SchoolResponse>("/modify_school/", {
        body: {
          school_cnpj: String(school.school_cnpj) || "",
          school_name: school.school_name || "",
          school_cep: String(school.school_cep) || "",
          school_board_email: school.school_board_email || "",
          school_link: school.school_link || "",
          link_active: school.link_active ?? false, // Garantir booleano
          school_type: school.school_type ?? 0, // Garantir número
          school_street: school.school_street || "",
          school_city: school.school_city || "",
          school_unit: school.school_unit || "",
          school_neighborhood: school.school_neighborhood || "",
        },
        headers: {},
      });

      // Se o status for 201, atualizar apenas o school_link no estado
      if (response && response.school_link && response.status_code === 201) {
        setSchoolLink(response?.school_link)
      }

      const statusMessages: Record<number,{ message: string; type: "success" | "error" | "info"; close: boolean }> = {
        200: { message: "Sucesso na atualização.", type: "success", close: true },
        201: { message: `Sucesso na criação: LINK gerado: ${response?.school_link || ""}`,type: "success", close: true },
      };

      const responseData = statusMessages[Number(response.status_code)] ?? { message: `Erro na modificação: ${response.status_code}`, type: "error", close: false };
      return responseData;
      
    } catch (error) {
      // console.error("Erro na requisição:", error);
      return { message: `Erro na modificação: ${String(error)}`, type: "error", close: false };
    } finally {
      setLoading(false);
    }
  };

  // Funcao para criar uma nova escola
  const handleNewSchool = () => {
    setDialogContent(<SchoolForm onCallback={handleCallback} school={undefined} />);
    setDialogOpen(true);
  };

  // Funcao para editar uma escola
  const handleEditSchool = (school: Ischools) => {
    setDialogContent(<SchoolForm school={school} onCallback={handleCallback} />);
    setDialogOpen(true);
  };

  // Funcao para cadastrar alunos
  const handleStudents = (school: Ischools) => {
    setDialogContent(<Students school={school} />)
    setDialogOpen(true);
  }

  const handleCallback = async (school: Ischools) => {
    const responses = await modifySchools(school);
    setSchools((prevData) => {
      const index = prevData.findIndex((item) => item.school_cnpj === school.school_cnpj);
      if (index !== -1) {
          // Atualiza a escola existente
          const updatedSchools = [...prevData];
          updatedSchools[index] = { ...prevData[index], ...school };
          return updatedSchools;
      }
      setUpdateSchool(school);
      return prevData;
      // Caso não exista, adiciona como nova escola, incluindo o valor de schoolLink
      // return [...prevData, { ...school, school_link: schoolLink }];
    });
    setAlertMessage({ message: responses.message, type: responses.type as "success" | "error" | "info", close: responses.close });
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogContent(null);
  };

  // Copiar link para área de transferência
  const handleCopyLink = (link: string) => {
    navigator.clipboard.writeText(link).then(
      () => {
        setAlertMessage({ message: 'Link copiado', type: "info", close: true });
      },
      (err) => {
        setAlertMessage({ message: `Erro ao copiar link: ${String(err)}`, type: "error", close: false });
      }
    );
  };

  // Mudar e atualizar o switch de link ativo
  const handleToggleActive = async (cnpj: string, isActive: boolean) => {
    let dataModify: Ischools | undefined;
    setSchools((prevSchools) => {
      const index = prevSchools.findIndex((school) => school.school_cnpj === cnpj);
      if (index === -1) return prevSchools;

      const updatedSchools = [...prevSchools];
      updatedSchools[index] = { ...updatedSchools[index], link_active: isActive };
      dataModify = updatedSchools[index];
      return updatedSchools;
    });

    if (!dataModify) {
      console.error("Não foi possível encontrar a escola para modificar.");
      return;
    }

    const responses = await modifySchools(dataModify);
    setAlertMessage({ message: responses.message, type: responses.type as "success" | "error" | "info", close: responses.close });
  };

  React.useEffect(() => {
    fetchSchools();
  }, []);

  React.useEffect(() => {
    setSchools((prevData) => {
      return [...prevData, { ...updateSchool, school_link: schoolLink }];
    });
  }, [schoolLink]);

  return (
    <>
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100vh" }}>
          <LinearProgressBar />
        </Box>
      ) : (
        <>
          {alertMessage && <AlertMessage key={Math.random()} message={alertMessage.message} type={alertMessage.type} auto_close={alertMessage.close} />}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh" }}>
            <IconButton color="primary" aria-label="adicionar escola" sx={{ marginBottom: 2 }} onClick={() => handleNewSchool()}>
              <AddBusinessIcon />
            </IconButton>
            <TableContainer component={Paper} sx={{ width: "70%", height: "70%", overflow: "auto" }}>
              <Table sx={{ minWidth: 650 }} size="small" aria-label="a dense table">
                <TableHead>
                  <TableRow>
                    <TableCell>CNPJ</TableCell>
                    <TableCell>Escola</TableCell>
                    <TableCell>Link</TableCell>
                    <TableCell>Link Ativo</TableCell>
                    <TableCell>Alunos</TableCell>
                    <TableCell>Editar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {schools.map((school) => (
                    <TableRow key={String(school.school_cnpj)}>
                      <TableCell>{school.school_cnpj}</TableCell>
                      <TableCell>{school.school_name}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ cursor: "pointer", color: "blue" }} onClick={() => handleCopyLink(`${getURL()}/${school.school_link}` || "")}>{`.../${school.school_link}`}</Typography>
                      </TableCell>
                      <TableCell>
                        <Switch size="small" checked={!!school.link_active} inputProps={{ "aria-label": "Link ativo" }} onChange={(event) => handleToggleActive(String(school.school_cnpj), event.target.checked)}/>
                      </TableCell>
                      <TableCell>
                        <IconButton color="primary" aria-label="Alunos" onClick={() => handleStudents(school)}>
                          <GroupsIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <IconButton color="primary" aria-label="Editar" onClick={() => handleEditSchool(school)}>
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
          {/* <CustomDialog open={dialogOpen} onClose={handleCloseDialog}>{dialogContent || <div />}</CustomDialog> */}
          <CustomBackDrop open={dialogOpen} onClose={handleCloseDialog}>{dialogContent || <div />}</CustomBackDrop>
        </>
      )}
    </>
  );  
};

export default SchoolTable;